import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import NoticeBar from 'components/NoticeBar'
import { selectCurrentUserIsAdminOrOwner } from 'ducks/currentUser/selectors'
import {
  doFetchKnowledgeBases,
  startMigration,
} from 'subapps/kb/actions/knowledge_bases'
import {
  selectKnowledgeBaseForInboxMigrationBanner,
  selectIsLoading,
  selectError,
  selectIsKbsLoading,
} from 'subapps/kb/selectors/knowledge_bases'
import { selectPrefersInstantDocsMigrationBanner } from 'selectors/app/selectAccountPreferences'
import { styles as emotionStyles } from './styles'

// Track if KBs have been fetched this session to avoid repeated API calls
let hasFetchedKbsThisSession = false

export default function KbMigrationBanner() {
  const dispatch = useDispatch()
  const isManager = useSelector(selectCurrentUserIsAdminOrOwner)
  const kbForBanner = useSelector(selectKnowledgeBaseForInboxMigrationBanner)
  const isLoading = useSelector(selectIsLoading)
  const isKbsLoading = useSelector(selectIsKbsLoading)
  const migrationError = useSelector(selectError)
  const isMigrationBannerEnabled = useSelector(
    selectPrefersInstantDocsMigrationBanner
  )
  // Fetch KBs once per session to ensure fresh migration status data
  useEffect(
    () => {
      if (!hasFetchedKbsThisSession && !isKbsLoading) {
        hasFetchedKbsThisSession = true
        dispatch(doFetchKnowledgeBases())
      }
    },
    [dispatch, isKbsLoading]
  )

  const handleInitiateMigration = () => {
    if (!kbForBanner || !kbForBanner.id) return
    dispatch(startMigration(kbForBanner.id))
  }

  const getMigrationUrl = () => {
    if (
      !kbForBanner ||
      !kbForBanner.migrationToken ||
      kbForBanner.migrationToken.trim() === ''
    ) {
      return null
    }
    return `https://groove-migration.instantdocs.com/${
      kbForBanner.migrationToken
    }`
  }

  const handleContinueMigration = () => {
    const migrationUrl = getMigrationUrl()
    if (migrationUrl) {
      window.open(migrationUrl, '_blank', 'noopener,noreferrer')
    }
  }

  // Only show for managers when migration banner is enabled and there's a KB to migrate
  if (!isMigrationBannerEnabled || !isManager || !kbForBanner) {
    return null
  }

  // Show error banner if there was a migration error
  if (migrationError) {
    return (
      <NoticeBar key="kb-migration-error" type="warning" dismissible={false}>
        <div css={emotionStyles.migrationContent}>
          <div css={emotionStyles.migrationText}>
            <strong>Migration Error:</strong> {migrationError}
          </div>
        </div>
      </NoticeBar>
    )
  }

  const buttonStyle = {
    cursor: isLoading ? 'wait' : 'pointer',
    color: 'white',
    backgroundColor: 'var(--color-monochrome-black)',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: 'var(--fontSize-large)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    opacity: isLoading ? 0.7 : 1,
  }

  const secondaryButtonStyle = {
    cursor: 'pointer',
    color: 'var(--color-monochrome-black)',
    backgroundColor: 'white',
    border: '1px solid var(--color-monochrome-black)',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: 'var(--fontSize-base)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }

  // Show banner based on migration status
  if (kbForBanner.migrationStatus === 'pending') {
    return (
      <NoticeBar key="kb-migration-pending" type="warning" dismissible={false}>
        <div css={emotionStyles.migrationContent}>
          <div css={emotionStyles.migrationText}>
            Your Groove Knowledge Base will be retired on Feb 1st, 2026. Start
            your migration to InstantDocs today.
          </div>
          <div css={emotionStyles.migrationActions}>
            <button
              type="button"
              onClick={handleInitiateMigration}
              disabled={isLoading}
              style={buttonStyle}
            >
              {isLoading ? 'Starting...' : 'Start Migration'}
            </button>
            <a
              href="https://help.instantdocs.com/doc/migrate-groove-to-instantdocs"
              target="_blank"
              rel="noopener noreferrer"
              style={secondaryButtonStyle}
            >
              Migration Guide
            </a>
            <a
              href="https://www.groovehq.com/blog/groove-to-instantdocs-kb"
              target="_blank"
              rel="noopener noreferrer"
              style={secondaryButtonStyle}
            >
              Read Announcement
            </a>
          </div>
        </div>
      </NoticeBar>
    )
  }

  if (kbForBanner.migrationStatus === 'initiated') {
    const migrationUrl = getMigrationUrl()
    return (
      <NoticeBar key="kb-migration-progress" type="warning" dismissible={false}>
        <div css={emotionStyles.migrationContent}>
          <div css={emotionStyles.migrationText}>
            Your Groove Knowledge Base will be retired on Feb 1st, 2026.
            Migration is currently in progress.
          </div>
          <div css={emotionStyles.migrationActions}>
            {migrationUrl && (
              <button
                type="button"
                onClick={handleContinueMigration}
                style={buttonStyle}
              >
                Continue migration
              </button>
            )}
            <a
              href="https://help.instantdocs.com/doc/migrate-groove-to-instantdocs"
              target="_blank"
              rel="noopener noreferrer"
              style={secondaryButtonStyle}
            >
              Migration Guide
            </a>
            <a
              href="https://www.groovehq.com/blog/groove-to-instantdocs-kb"
              target="_blank"
              rel="noopener noreferrer"
              style={secondaryButtonStyle}
            >
              Read Announcement
            </a>
          </div>
        </div>
      </NoticeBar>
    )
  }

  return null
}
